import useSound from "use-sound";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { DefaultRouteProps } from "@app/App";

import { QUIZZ_SERVER_URL } from "@app/config";

import { AvatarAssetType, SkinAssetType } from "@app/api/models/user";
import { useDefaultProducts } from "@app/api/product";

import Context from "@app/contexts/Context";

import { User } from "@app/types/user";

import { GameImages } from "@app/assets/images/game/game";

import styles from "@app/assets/styles/end-game.module.scss";

const sortByScore = (a: any, b: any) => {
    if (a.score == b.score) {
        return 0;
    }

    return (a.score > b.score) ? -1 : 1;
};

export default function EndGame(props: DefaultRouteProps) {
    const { receiverContext } = props;
    const { t } = useTranslation();

    const { data } = useDefaultProducts();

    const bottomWrapperRef = useRef<HTMLDivElement>(null);
    let geniusTimeout = useRef<NodeJS.Timeout>();
    const params = useParams();

    const winSoundNumber: number = params?.winSoundNumber ? +params.winSoundNumber : 0;

    const navigate = useNavigate();
    const [players, setPlayers] = useState<User[]>();

    const {
        ioSocket,
        roomId,
        setRoomId,
        setIoSocket,
    } = useContext(Context);

    // Sound
    const soundManager = {
        victory: useSound(require("../assets/sound/game/victory.m4a"), { loop: true, autoplay: true }),
        genius: [
            useSound(require("../assets/sound/genius/win/win1.m4a")),
            useSound(require("../assets/sound/genius/win/win2.m4a")),
            useSound(require("../assets/sound/genius/win/win3.m4a")),
            useSound(require("../assets/sound/genius/win/win4.m4a")),
        ],
    };

    const winnerImage = data?.characters
        .find((character) => {
            const id = character.id?.split("-")[0];
            if (id) return id === players?.[0].character
        })
        ?.products.find((product) => product.id === players?.[0].characterSkinId)
        ?.assets.find((asset) => asset.type === SkinAssetType.Win);

    //Query
    useQuery(["get", "players"], async () => {
            const res = await fetch(`${QUIZZ_SERVER_URL}/rooms/users/room-${roomId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
            });
            const players: User[] = await res.json();
            players.sort(sortByScore);
            setPlayers(players);
        },
        { keepPreviousData: false }
    );

    useEffect(() => {
        if (winSoundNumber) {
            geniusTimeout.current = setTimeout(() => {
                soundManager.genius[winSoundNumber][0]();
            }, 4000);
        }

        function exitRoom() {
            clearTimeout(geniusTimeout.current);
            soundManager.victory[1].stop();
            if (winSoundNumber) {
                soundManager.genius[winSoundNumber][1].stop();
            }
            setRoomId(undefined);
            if (ioSocket) {
                ioSocket.emit("tvDisconnected");
                ioSocket.disconnect();
            }
            if (receiverContext) {
                receiverContext.stop();
            }
            setIoSocket(undefined);
            navigate("/");
        };

        if (ioSocket) {
            ioSocket.on("deleteRoom", exitRoom);
        }
        return () => {
            soundManager.victory[1].stop();
            if (winSoundNumber) {
                soundManager.genius[winSoundNumber][1].stop();
            }
            clearTimeout(geniusTimeout.current);
            if (ioSocket) {
                ioSocket.off("deleteRoom", exitRoom);
            }
        }
    }, [soundManager, winSoundNumber]);

    useEffect(() => {
        if (bottomWrapperRef && players) {
            const scrollLimit = window.innerWidth * 0.14 * (players.length - 8) - 50;
            setInterval(() => {
                if (bottomWrapperRef.current?.scrollLeft && bottomWrapperRef.current.scrollLeft > scrollLimit) {
                    bottomWrapperRef.current?.scrollTo({
                        left: 0,
                        behavior: "smooth"
                    });
                } else {
                    bottomWrapperRef.current?.scrollBy({
                        left: window.innerWidth * 0.14,
                        behavior: "smooth"
                    });
                }
            }, 2000);
        }
    }, [bottomWrapperRef, players]);

    return (
    <div className={styles.endGameContainer}>
        <img src={GameImages.characterWin.url} alt=""/>
        <img style={{top: 0}} src={winnerImage?.url} alt=""/>
        <img src={GameImages.buzzerWin.url} alt=""/>
        <img src={GameImages.winBackground.url} alt=""/>
        <div className={styles.endGameInfosWrapper}>
            <div className={styles.topWrapper}>
                <div className={styles.winnerWrapper} style={{ background: `url(${GameImages.winInsert.url}) center / contain no-repeat` }}>
                    <div className={styles.firstText}>{t("endGame.congratulations")}</div>
                    {players && <div className={styles.winnerName}>{players[0].name}</div>}
                    <div className={styles.secondText}>{t("endGame.win")}</div>
                </div>
                {players && <div className={styles.winnerScore}>{t("endGame.score")} {players[0].score}</div>}
            </div>
            <div className={styles.bottomWrapper} ref={bottomWrapperRef}>
                {players && players.map((player, index) => {
                        const characterImg = data?.avatar
                            ?.products.find((product) => product.id === player.avatarSkinId)
                            ?.assets.find((asset) => asset.type === AvatarAssetType.Avatar);
                        return (
                            <div key={index} className={styles.playerWrapper} style={{ minWidth: window.innerWidth * 0.14 }}>
                                <img src={characterImg?.url} alt=""/>
                                <div className={styles.star} style={{ background: `url(${GameImages.star.url}) center / contain no-repeat` }}>{index + 1}</div>
                                <div className={styles.playerText}>
                                    <div>{player.name}</div>
                                    <div>{t("endGame.points")} {player.score}</div>
                                </div>
                            </div>
                        );
                    })
                }
            </div>
        </div>
    </div>
    );
}
